import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Grid,
  Spinner,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { MultiTreeDropdown } from '@/components/forms/TreeDropdownMultiple'
import {
  createItemSchema,
  updateItemSchema,
  type CreateItemFormData,
  type UpdateItemFormData,
} from '@/lib/validations/auth'

interface ItemFormProps {
  itemId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type ItemFormValues = CreateItemFormData | UpdateItemFormData

export function ItemForm({ itemId, onSuccess, onCancel }: ItemFormProps) {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()
  const [selectedSubcategoryLabels, setSelectedSubcategoryLabels] = useState<Record<number, string>>({})

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!itemId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ItemFormValues>({
    resolver: zodResolver(isEditMode ? updateItemSchema : createItemSchema),
    defaultValues: {
      descriptions: '',
      subcategory_id: [],
    },
  })

  const loadOptions = useCallback(
    (parentId: number | null, callback: (data: any[]) => void) => {
      const url = `/category-management/categories/dropdown${parentId ? `/${parentId}` : ''}`
      apiClient
        .get(url)
        .then(({ data }) => {
          let categoryData = data?.data || data || []
          categoryData = Array.isArray(categoryData) ? categoryData : []

          if (isEditMode && itemId) {
            const filterCategory = (items: any[]): any[] =>
              items
                .filter((item) => item.id !== itemId)
                .map((item) => ({
                  ...item,
                  children: item.children ? filterCategory(item.children) : item.children,
                }))
            categoryData = filterCategory(categoryData)
          }

          callback(categoryData)
        })
        .catch((error: any) => {
          showError(error)
          callback([])
        })
    },
    [itemId, isEditMode, showError]
  )

  useEffect(() => {
    if (isEditMode && itemId) {
      setIsLoading(true)
      apiClient
        .get(`/category-management/items/${itemId}`)
        .then(({ data }) => {
          const itemData = data?.data || data || {}

          let subcategoryIds: number[] = []
            if (itemData.subcategory_id) {
            subcategoryIds = Array.isArray(itemData.subcategory_id)
                ? itemData.subcategory_id.map((id: number | string) => Number(id)).filter((id: number) => !isNaN(id))
                : [Number(itemData.subcategory_id)].filter((id: number) => !isNaN(id))
            } else if (itemData.subcategory?.id) {
            subcategoryIds = [itemData.subcategory.id]
            }

            if (subcategoryIds.length > 0) {
              Promise.all(
                subcategoryIds.map((id) =>
                  apiClient
                    .get(`/category-management/categories/${id}`)
                    .then(({ data }) => ({ id, name: data?.data?.name || data?.name || '' }))
                    .catch(() => ({ id, name: '' }))
                )
              ).then((rows) => {
                const map: Record<number, string> = {}
                rows.forEach((row) => {
                  if (row.name) {
                    map[row.id] = row.name
                  }
                })
                setSelectedSubcategoryLabels(map)
              })
            } else {
              setSelectedSubcategoryLabels({})
            }

            reset({
            name: itemData.name || '',
            code: itemData.code || '',
            descriptions: itemData.descriptions || '',
            subcategory_id: subcategoryIds,
            })
        })
        .catch((error: any) => {
          showError(error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [itemId, isEditMode, reset, showError])

  const onSubmit = async (data: ItemFormValues) => {
    setIsSubmitting(true)

    try {
        const ids = Array.isArray(data.subcategory_id)
        ? data.subcategory_id
            .map((id) => Number(id))
            .filter((id) => !isNaN(id))
        : []

        const payload = {
            name: data.name,
            code: data.code,
            descriptions: data.descriptions || null,
            subcategory_id: data.subcategory_id,
        }

        console.log('FORM DATA:', data)

        if (isEditMode) {
        await apiClient.put(`/category-management/items/${itemId}`, payload)
        showSuccess('Success', 'Item updated successfully')
        } else {
        await apiClient.post('/category-management/items', payload)
        showSuccess('Success', 'Item created successfully')
        }

        onSuccess?.() ?? navigate('/category-management/items')
    } catch (error: any) {
        showError(error)
    } finally {
        setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/category-management/items')
    }
  }

  type SubcategoryIds = number[]

  if (isLoading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading item...</Text>
      </VStack>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input {...register('name')} placeholder="e.g. Technology, Business, Health" />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            <FormHelperText>The name is how it appears on your site.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Code <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.code}>
            <Input {...register('code')} placeholder="e.g. technology, business, health" />
            <FormErrorMessage>{errors.code?.message}</FormErrorMessage>
            <FormHelperText>The code is used in URLs. Only letters, numbers, and hyphens.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Description
          </FormLabel>
          <FormControl isInvalid={!!errors.descriptions}>
            <Textarea
              {...register('descriptions')}
              placeholder="Enter category description..."
              rows={4}
            />
            <FormErrorMessage>{errors.descriptions?.message}</FormErrorMessage>
            <FormHelperText>Optional description for this category.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Parent Category / Subcategory
          </FormLabel>
          <FormControl>
            <MultiTreeDropdown
            value={(watch('subcategory_id') ?? []) as number[]}
            loadOptions={loadOptions}
            valueLabels={selectedSubcategoryLabels}
            placeholder="Select one or more categories (optional)"
            onChange={(ids: number[]) => {
                setValue('subcategory_id', ids, {
                shouldValidate: true,
                shouldDirty: true,
                })
            }}
            />
            <FormHelperText>
              Multiple selection is supported. Selected values are shown by category name.
            </FormHelperText>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          <Button variant="outline" onClick={handleCancel} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Item'}
            {isSubmitting && (
              <Text as="span" ml={2} fontSize="sm">
                ...
              </Text>
            )}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}